/**
 * Pure helper functions for building the client-offer availability grid
 * from API AvailabilityPeriodCell[] data.
 */
import type { AvailabilityPeriodCell } from '@ooh/contracts';
import type { CellState } from '../../../availability/lib/availability-types';
import { formatShortMonth } from '@ooh/ui/lib/date-format';

/**
 * Convert API AvailabilityPeriodCell entries to a CellState for the grid.
 * Maps API status (uppercase) to client CellState (lowercase). Only three states
 * are exposed to clients: free, sold, reserved-other-soft (the client-safe collapsed
 * "reserved" look — no soft/hard distinction is exposed to clients, per the
 * availability-status-colors change's Non-Goals; 'reserved-other-soft' is used only as
 * the generic-reserved representative literal).
 */
export function getCellStateFromPeriod(period: AvailabilityPeriodCell): CellState {
  // If any entry is SOLD, that dominates
  if (period.entries.some((e) => e.status === 'SOLD')) return 'sold';
  // If any entry is not FREE, treat as reserved
  if (period.entries.some((e) => e.status !== 'FREE')) return 'reserved-other-soft';
  return 'free';
}

/**
 * Format a short label for a period. e.g. "01–15 kwi". Shared by raw
 * AvailabilityPeriodCell[] callers and the client-offer-review-v2 period axis
 * (build-review-periods.ts), which only needs `start`/`end` — not the full cell shape.
 *
 * A single-day period (start === end, always true for DAY granularity — see
 * enumeratePeriods's enumerateDayPeriods) renders as just "{day} {month}"
 * instead of a redundant "{day}–{day} {month}" range.
 *
 * `language` defaults to `'pl'` for callers that don't yet track a live locale (see
 * `date-format.ts`'s `APP_LOCALE`), but every real caller in this feature threads
 * through the live `i18n.language` — see `buildReviewPeriods`/`buildReviewPeriodAxis`
 * (build-review-periods.ts). Bug fix: this used to render Polish month abbreviations
 * from a hardcoded array regardless of `i18n.language`, even though the app ships both
 * `en` and `pl` locales.
 */
export function formatPeriodLabel(period: { start: string; end: string }, language = 'pl'): string {
  const start = new Date(period.start);
  const startDay = String(start.getDate()).padStart(2, '0');
  const month = formatShortMonth(start, language);
  if (period.start === period.end) {
    return `${startDay} ${month}`;
  }
  const end = new Date(period.end);
  const endDay = String(end.getDate()).padStart(2, '0');
  return `${startDay}–${endDay} ${month}`;
}
